/**
 * BillingInvoiceDetail — line items + payments + admin operations.
 *
 * Operations: issue, send-reminder, record-payment, approve-pending,
 * refund, void, apply-late-fee, adjust. Every action goes through the
 * billing router which audit-logs the mutation.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Send,
  Bell,
  CreditCard,
  Receipt,
  Slash,
  Pencil,
  AlertTriangle,
  Printer,
} from "lucide-react";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { StatusPill, money } from "./BillingDashboard";

const METHODS = ["bank_transfer", "cash", "check", "card_offline", "subsidy", "other"];

function Modal({ open, onClose, title, children, testid }) {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid={testid}
      onClick={onClose}
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-display text-xl text-[#1A1B2E]">{title}</h3>
        {children}
      </div>
    </div>
  );
}

export default function BillingInvoiceDetail() {
  const { t } = useI18n();
  const { id } = useParams();
  const nav = useNavigate();
  const [inv, setInv] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [recordOpen, setRecordOpen] = useState(false);
  const [recordForm, setRecordForm] = useState({ amount: "", method: "bank_transfer", note: "" });
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundForm, setRefundForm] = useState({ payment_id: "", amount: "", reason: "" });
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustForm, setAdjustForm] = useState({ kind: "credit", amount: "", label: "", description: "" });
  const [subsidyOpen, setSubsidyOpen] = useState(false);
  const [subsidyForm, setSubsidyForm] = useState({ payer_id: "", amount: "", reference: "" });
  const [subsidyPayers, setSubsidyPayers] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [invR, paysR, payersR] = await Promise.all([
        api.get(`/admin/billing/invoices/${id}`),
        api.get(`/admin/billing/invoices/${id}/payments-list`).catch(() => null),
        api.get(`/admin/billing/subsidy-payers`).catch(() => null),
      ]);
      setInv(invR.data);
      setRecordForm((f) => ({ ...f, amount: String(invR.data.amount_due || "") }));
      setPayments(paysR?.data || []);
      const payers = (payersR?.data || []).filter((p) => p.active !== false);
      setSubsidyPayers(payers);
      setSubsidyForm((f) => ({ ...f, payer_id: payers[0]?.id || "" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("billing.errors.generic"));
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const op = async (fn, successMsg) => {
    try {
      await fn();
      toast.success(successMsg || t("billing.common.saved"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("billing.errors.generic"));
    }
  };

  if (loading || !inv) {
    return (
      <main className="p-6 lg:p-10">
        <p className="font-serif italic text-[#525252]">{t("billing.common.loading")}</p>
      </main>
    );
  }

  return (
    <main className="p-6 lg:p-10 space-y-8" data-testid="admin-billing-invoice-detail">
      <Link
        to="/admin/billing/invoices"
        className="inline-flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#1A1B2E]"
        data-testid="billing-detail-back"
      >
        <ArrowLeft size={14} strokeWidth={1.6} />
        {t("billing.invoice_detail.back_to_invoices")}
      </Link>

      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="eyebrow">{t("billing.invoice_detail.title_prefix")}</span>
          <h1 className="mt-2 font-display text-3xl text-[#1A1B2E]" data-testid="billing-detail-number">
            {inv.invoice_number}
          </h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            {t("billing.invoice_detail.for_family")} {inv.parent_name} · {inv.child_name}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill status={inv.status} />
          <a
            href={`/billing/invoices/${inv.id}/print`}
            target="_blank"
            rel="noreferrer"
            data-testid="billing-detail-print"
            className="inline-flex items-center gap-2 text-sm text-[#1F3A2A] border border-[#EEF0F4] rounded-full px-3 py-1.5 hover:bg-[#FAFAFB]"
          >
            <Printer size={14} strokeWidth={1.6} />
            {t("billing.invoice_detail.print_pdf_cta")}
          </a>
        </div>
      </header>

      <section className="grid sm:grid-cols-4 gap-3 text-sm">
        <Meta label={t("billing.invoice_detail.period_label")} value={`${inv.period_start || "—"} → ${inv.period_end || "—"}`} />
        <Meta label={t("billing.invoice_detail.due_label")} value={inv.due_at || "—"} />
        <Meta label={t("billing.invoice_detail.issued_label")} value={inv.issued_at ? inv.issued_at.slice(0, 10) : "—"} />
        <Meta label="Room" value={inv.room_name || "—"} />
      </section>

      <section className="rounded-3xl bg-white border border-[#EEF0F4] overflow-hidden">
        <div className="px-6 pt-5 pb-3 border-b border-[#EEF0F4]">
          <h3 className="font-display text-lg text-[#1A1B2E]">{t("billing.invoice_detail.line_items")}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-[#6B7280] uppercase tracking-[0.1em] text-[10px]">
              <tr>
                <th className="px-6 py-3">Label</th>
                <th className="px-6 py-3 text-right">Qty</th>
                <th className="px-6 py-3 text-right">Unit</th>
                <th className="px-6 py-3 text-right">Subtotal</th>
                <th className="px-6 py-3 text-right">Tax</th>
                <th className="px-6 py-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {(inv.line_items || []).map((li) => (
                <tr key={li.id} className="border-t border-[#EEF0F4]">
                  <td className="px-6 py-3">
                    <div className="font-medium text-[#1A1B2E]">{li.label}</div>
                    {li.description && (
                      <div className="text-xs text-[#9CA3AF] mt-0.5">{li.description}</div>
                    )}
                  </td>
                  <td className="px-6 py-3 text-right tabular-nums">{li.quantity}</td>
                  <td className="px-6 py-3 text-right tabular-nums">{money(li.unit_amount, inv.currency)}</td>
                  <td className="px-6 py-3 text-right tabular-nums">{money(li.subtotal, inv.currency)}</td>
                  <td className="px-6 py-3 text-right tabular-nums">{money(li.tax_amount, inv.currency)}</td>
                  <td className="px-6 py-3 text-right tabular-nums font-medium">{money(li.total, inv.currency)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="text-sm">
              <tr className="border-t border-[#EEF0F4]">
                <td colSpan={5} className="px-6 py-2 text-right text-[#6B7280]">{t("billing.invoice_detail.subtotal")}</td>
                <td className="px-6 py-2 text-right tabular-nums">{money(inv.subtotal, inv.currency)}</td>
              </tr>
              <tr>
                <td colSpan={5} className="px-6 py-2 text-right text-[#6B7280]">{t("billing.invoice_detail.tax_total")}</td>
                <td className="px-6 py-2 text-right tabular-nums">{money(inv.tax_total, inv.currency)}</td>
              </tr>
              <tr className="border-t border-[#EEF0F4]">
                <td colSpan={5} className="px-6 py-3 text-right font-medium text-[#1A1B2E]">{t("billing.invoice_detail.total")}</td>
                <td className="px-6 py-3 text-right tabular-nums font-display text-lg text-[#1A1B2E]">
                  {money(inv.total, inv.currency)}
                </td>
              </tr>
              <tr>
                <td colSpan={5} className="px-6 py-2 text-right text-[#1F8A3F]">{t("billing.invoice_detail.amount_paid")}</td>
                <td className="px-6 py-2 text-right tabular-nums text-[#1F8A3F]">{money(inv.amount_paid, inv.currency)}</td>
              </tr>
              <tr className="border-t border-[#EEF0F4] bg-[#FAF9F1]/40">
                <td colSpan={5} className="px-6 py-3 text-right font-display text-base text-[#1A1B2E]">
                  {t("billing.invoice_detail.amount_due")}
                </td>
                <td className="px-6 py-3 text-right tabular-nums font-display text-xl text-[#1F3A2A]">
                  {money(inv.amount_due, inv.currency)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      <section className="flex flex-wrap items-center gap-2">
        {inv.status === "draft" && (
          <button
            onClick={() =>
              op(() => api.post(`/admin/billing/invoices/${id}/issue`), t("billing.messages.invoice_issued"))
            }
            data-testid="billing-detail-issue"
            className="inline-flex items-center gap-2 rounded-full bg-[#1F3A2A] text-white text-sm font-medium px-4 py-2 hover:bg-[#132C17]"
          >
            <Send size={14} strokeWidth={1.8} />
            {t("billing.invoice_detail.issue_cta")}
          </button>
        )}
        {(inv.status === "issued" || inv.status === "partial" || inv.status === "overdue") && (
          <button
            onClick={() =>
              op(
                () => api.post(`/admin/billing/invoices/${id}/reminder`),
                t("billing.messages.invoice_reminder_sent")
              )
            }
            data-testid="billing-detail-reminder"
            className="inline-flex items-center gap-2 rounded-full bg-white border border-[#EEF0F4] text-sm font-medium text-[#1A1B2E] px-4 py-2 hover:bg-[#FAFAFB]"
          >
            <Bell size={14} strokeWidth={1.8} />
            {t("billing.invoice_detail.send_reminder_cta")}
          </button>
        )}
        {inv.amount_due > 0 && inv.status !== "void" && (
          <button
            onClick={() => setRecordOpen(true)}
            data-testid="billing-detail-record-payment"
            className="inline-flex items-center gap-2 rounded-full bg-[#1F3A2A] text-white text-sm font-medium px-4 py-2 hover:bg-[#132C17]"
          >
            <CreditCard size={14} strokeWidth={1.8} />
            {t("billing.invoice_detail.record_payment_cta")}
          </button>
        )}
        {(inv.status === "issued" || inv.status === "overdue") && !inv.late_fee_applied && (
          <button
            onClick={() =>
              op(
                () => api.post(`/admin/billing/invoices/${id}/late-fee`),
                t("billing.messages.late_fee_applied")
              )
            }
            data-testid="billing-detail-late-fee"
            className="inline-flex items-center gap-2 rounded-full bg-white border border-[#FCD3D3] text-sm font-medium text-[#A11D1D] px-4 py-2 hover:bg-[#FCEEEE]"
          >
            <AlertTriangle size={14} strokeWidth={1.8} />
            {t("billing.invoice_detail.apply_late_fee_cta")}
          </button>
        )}
        <button
          onClick={() => setAdjustOpen(true)}
          data-testid="billing-detail-adjust"
          className="inline-flex items-center gap-2 rounded-full bg-white border border-[#EEF0F4] text-sm font-medium text-[#1A1B2E] px-4 py-2 hover:bg-[#FAFAFB]"
        >
          <Pencil size={14} strokeWidth={1.8} />
          {t("billing.invoice_detail.adjust_cta")}
        </button>
        {inv.status !== "paid" && inv.status !== "void" && (
          <button
            onClick={() =>
              op(
                () => api.post(`/admin/billing/invoices/${id}/void`),
                t("billing.messages.invoice_voided")
              )
            }
            data-testid="billing-detail-void"
            className="inline-flex items-center gap-2 rounded-full bg-white border border-[#EEF0F4] text-sm font-medium text-[#6B7280] px-4 py-2 hover:bg-[#FAFAFB]"
          >
            <Slash size={14} strokeWidth={1.8} />
            {t("billing.invoice_detail.void_cta")}
          </button>
        )}
      </section>

      <section className="rounded-3xl bg-white border border-[#EEF0F4] p-6">
        <h3 className="font-display text-lg text-[#1A1B2E]">{t("billing.invoice_detail.payments_section")}</h3>
        {payments.length === 0 ? (
          <p className="mt-4 font-serif italic text-[#9CA3AF]" data-testid="billing-detail-payments-empty">
            {t("billing.invoice_detail.no_payments")}
          </p>
        ) : (
          <ul className="mt-4 space-y-3">
            {payments.map((p) => (
              <li
                key={p.id}
                data-testid={`billing-payment-row-${p.id}`}
                className="flex items-center justify-between gap-3 rounded-xl border border-[#EEF0F4] px-4 py-3"
              >
                <div>
                  <div className="text-sm text-[#1A1B2E]">
                    {money(p.amount, p.currency)} · {t(`billing.method.${p.method}`)}
                  </div>
                  <div className="text-xs text-[#6B7280]">
                    {p.received_at?.slice(0, 16)?.replace("T", " ")} · {p.recorded_by_name}{" "}
                    {p.parent_submitted ? "(parent submitted)" : ""}
                  </div>
                  {p.note && <div className="text-xs text-[#9CA3AF] mt-1">{p.note}</div>}
                </div>
                <div className="flex items-center gap-2">
                  <StatusPill status={p.status} />
                  {p.status === "pending_review" && (
                    <button
                      onClick={() =>
                        op(
                          () =>
                            api.post(
                              `/admin/billing/invoices/${id}/payments/${p.id}/approve`
                            ),
                          t("billing.messages.payment_approved")
                        )
                      }
                      data-testid={`billing-payment-approve-${p.id}`}
                      className="text-xs font-medium text-[#1F3A2A] hover:underline"
                    >
                      {t("billing.invoice_detail.approve_payment_cta")}
                    </button>
                  )}
                  {(p.status === "succeeded" || p.status === "partially_refunded") && (
                    <button
                      onClick={() => {
                        setRefundForm({ payment_id: p.id, amount: String(p.amount - p.refunded_amount), reason: "" });
                        setRefundOpen(true);
                      }}
                      data-testid={`billing-payment-refund-${p.id}`}
                      className="text-xs font-medium text-[#A11D1D] hover:underline"
                    >
                      {t("billing.invoice_detail.refund_payment_cta")}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Subsidy splits */}
      <section
        className="rounded-3xl bg-white border border-[#EEF0F4] p-6"
        data-testid="billing-subsidy-section"
      >
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-display text-lg text-[#1A1B2E]">
            {t("billing.invoice_detail.subsidy_splits_title")}
          </h3>
          <button
            onClick={() => setSubsidyOpen(true)}
            data-testid="billing-detail-add-subsidy"
            className="text-xs font-medium text-[#1F3A2A] hover:underline"
            disabled={subsidyPayers.length === 0}
          >
            + add split
          </button>
        </div>
        {(inv.subsidy_splits || []).length === 0 ? (
          <p
            className="mt-3 font-serif italic text-[#9CA3AF]"
            data-testid="billing-subsidy-empty"
          >
            No subsidy splits attached. Family share is the full balance.
          </p>
        ) : (
          <ul className="mt-4 space-y-3">
            {inv.subsidy_splits.map((s) => (
              <li
                key={s.id}
                data-testid={`billing-subsidy-${s.id}`}
                className="flex items-center justify-between gap-3 rounded-xl border border-[#EEF0F4] px-4 py-3"
              >
                <div>
                  <div className="text-sm font-medium text-[#1A1B2E]">
                    {s.payer_name}
                  </div>
                  <div className="text-xs text-[#6B7280]">
                    {money(s.amount, inv.currency)}
                    {s.reference ? ` · ref ${s.reference}` : ""}
                    {s.received_at ? ` · received ${s.received_at.slice(0, 10)}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusPill status={s.status === "received" ? "paid" : "pending"} />
                  {s.status !== "received" && (
                    <>
                      <button
                        onClick={() =>
                          op(
                            () =>
                              api.post(
                                `/admin/billing/invoices/${id}/subsidy-splits/${s.id}/receive`
                              ),
                            "Subsidy marked as received."
                          )
                        }
                        data-testid={`billing-subsidy-receive-${s.id}`}
                        className="text-xs font-medium text-[#1F3A2A] hover:underline"
                      >
                        Mark received
                      </button>
                      <button
                        onClick={() =>
                          op(
                            () =>
                              api.delete(
                                `/admin/billing/invoices/${id}/subsidy-splits/${s.id}`
                              ),
                            "Split removed."
                          )
                        }
                        data-testid={`billing-subsidy-remove-${s.id}`}
                        className="text-xs text-[#A11D1D] hover:underline"
                      >
                        Remove
                      </button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
        {(inv.subsidy_splits || []).length > 0 && (
          <div className="mt-4 text-sm text-[#6B7280]">
            Family share:{" "}
            <span className="font-medium text-[#1A1B2E] tabular-nums">
              {money(inv.family_share, inv.currency)}
            </span>{" "}
            · Subsidy share:{" "}
            <span className="font-medium text-[#3D5C46] tabular-nums">
              {money(inv.subsidy_share, inv.currency)}
            </span>
          </div>
        )}
      </section>

      {/* Record-payment modal */}
      <Modal
        open={recordOpen}
        onClose={() => setRecordOpen(false)}
        title={t("billing.invoice_detail.record_payment_modal_title")}
        testid="billing-record-modal"
      >
        <p className="mt-1 text-sm text-[#6B7280]">
          {t("billing.invoice_detail.record_payment_modal_blurb")}
        </p>
        <div className="mt-5 space-y-4">
          <label className="block">
            <span className="eyebrow">{t("billing.invoice_detail.record_payment_amount")}</span>
            <input
              type="number"
              step="0.01"
              data-testid="billing-record-amount"
              value={recordForm.amount}
              onChange={(e) => setRecordForm({ ...recordForm, amount: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm focus:outline-none focus:border-[#1F3A2A]"
            />
          </label>
          <label className="block">
            <span className="eyebrow">{t("billing.invoice_detail.record_payment_method")}</span>
            <select
              data-testid="billing-record-method"
              value={recordForm.method}
              onChange={(e) => setRecordForm({ ...recordForm, method: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm bg-white focus:outline-none focus:border-[#1F3A2A]"
            >
              {METHODS.map((m) => (
                <option key={m} value={m}>{t(`billing.method.${m}`)}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="eyebrow">{t("billing.invoice_detail.record_payment_note")}</span>
            <input
              type="text"
              data-testid="billing-record-note"
              value={recordForm.note}
              onChange={(e) => setRecordForm({ ...recordForm, note: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm focus:outline-none focus:border-[#1F3A2A]"
            />
          </label>
        </div>
        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            onClick={() => setRecordOpen(false)}
            className="text-sm text-[#6B7280] hover:text-[#1A1B2E]"
          >
            {t("billing.common.cancel")}
          </button>
          <button
            onClick={() =>
              op(
                () =>
                  api.post(`/admin/billing/invoices/${id}/payments`, {
                    amount: Number(recordForm.amount),
                    method: recordForm.method,
                    note: recordForm.note || null,
                  }),
                t("billing.messages.payment_recorded")
              ).then(() => setRecordOpen(false))
            }
            data-testid="billing-record-submit"
            className="inline-flex items-center gap-2 rounded-full bg-[#1F3A2A] text-white text-sm font-medium px-4 py-2 hover:bg-[#132C17]"
          >
            {t("billing.invoice_detail.record_payment_submit")}
          </button>
        </div>
      </Modal>

      {/* Refund modal */}
      <Modal
        open={refundOpen}
        onClose={() => setRefundOpen(false)}
        title={t("billing.invoice_detail.refund_modal_title")}
        testid="billing-refund-modal"
      >
        <div className="mt-5 space-y-4">
          <label className="block">
            <span className="eyebrow">{t("billing.invoice_detail.refund_amount")}</span>
            <input
              type="number"
              step="0.01"
              data-testid="billing-refund-amount"
              value={refundForm.amount}
              onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm focus:outline-none focus:border-[#1F3A2A]"
            />
          </label>
          <label className="block">
            <span className="eyebrow">{t("billing.invoice_detail.refund_reason")}</span>
            <textarea
              rows={3}
              data-testid="billing-refund-reason"
              value={refundForm.reason}
              onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm focus:outline-none focus:border-[#1F3A2A]"
            />
          </label>
        </div>
        <div className="mt-6 flex items-center justify-end gap-3">
          <button onClick={() => setRefundOpen(false)} className="text-sm text-[#6B7280] hover:text-[#1A1B2E]">
            {t("billing.common.cancel")}
          </button>
          <button
            onClick={() =>
              op(
                () =>
                  api.post(
                    `/admin/billing/invoices/${id}/payments/${refundForm.payment_id}/refund`,
                    { amount: Number(refundForm.amount), reason: refundForm.reason }
                  ),
                t("billing.messages.payment_refunded")
              ).then(() => setRefundOpen(false))
            }
            data-testid="billing-refund-submit"
            className="inline-flex items-center gap-2 rounded-full bg-[#A11D1D] text-white text-sm font-medium px-4 py-2 hover:bg-[#8B1818]"
          >
            {t("billing.invoice_detail.refund_submit")}
          </button>
        </div>
      </Modal>

      {/* Adjust modal */}
      <Modal
        open={adjustOpen}
        onClose={() => setAdjustOpen(false)}
        title={t("billing.invoice_detail.adjust_modal_title")}
        testid="billing-adjust-modal"
      >
        <div className="mt-5 space-y-4">
          <div className="flex items-center gap-2">
            {["credit", "debit", "writeoff"].map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setAdjustForm({ ...adjustForm, kind: k })}
                data-testid={`billing-adjust-kind-${k}`}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                  adjustForm.kind === k
                    ? "bg-[#1F3A2A] text-white border-[#1F3A2A]"
                    : "bg-white text-[#6B7280] border-[#EEF0F4]"
                }`}
              >
                {t(`billing.invoice_detail.adjust_kind_${k}`)}
              </button>
            ))}
          </div>
          <label className="block">
            <span className="eyebrow">Amount</span>
            <input
              type="number"
              step="0.01"
              data-testid="billing-adjust-amount"
              value={adjustForm.amount}
              onChange={(e) => setAdjustForm({ ...adjustForm, amount: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm focus:outline-none focus:border-[#1F3A2A]"
            />
          </label>
          <label className="block">
            <span className="eyebrow">{t("billing.invoice_detail.adjust_label")}</span>
            <input
              type="text"
              data-testid="billing-adjust-label"
              value={adjustForm.label}
              onChange={(e) => setAdjustForm({ ...adjustForm, label: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm focus:outline-none focus:border-[#1F3A2A]"
            />
          </label>
          <label className="block">
            <span className="eyebrow">{t("billing.invoice_detail.adjust_description")}</span>
            <textarea
              rows={2}
              data-testid="billing-adjust-description"
              value={adjustForm.description}
              onChange={(e) => setAdjustForm({ ...adjustForm, description: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm focus:outline-none focus:border-[#1F3A2A]"
            />
          </label>
        </div>
        <div className="mt-6 flex items-center justify-end gap-3">
          <button onClick={() => setAdjustOpen(false)} className="text-sm text-[#6B7280] hover:text-[#1A1B2E]">
            {t("billing.common.cancel")}
          </button>
          <button
            onClick={() =>
              op(
                () =>
                  api.post(`/admin/billing/invoices/${id}/adjust`, {
                    kind: adjustForm.kind,
                    amount: Number(adjustForm.amount),
                    label: adjustForm.label,
                    description: adjustForm.description || null,
                  }),
                t("billing.messages.adjust_applied")
              ).then(() => setAdjustOpen(false))
            }
            data-testid="billing-adjust-submit"
            className="inline-flex items-center gap-2 rounded-full bg-[#1F3A2A] text-white text-sm font-medium px-4 py-2 hover:bg-[#132C17]"
          >
            {t("billing.invoice_detail.adjust_submit")}
          </button>
        </div>
      </Modal>

      {/* Subsidy split modal */}
      <Modal
        open={subsidyOpen}
        onClose={() => setSubsidyOpen(false)}
        title="Add subsidy split"
        testid="billing-subsidy-modal"
      >
        <p className="mt-1 text-sm text-[#6B7280]">
          Attach a third-party payer (government voucher, employer, etc.). The
          family share decreases by this amount.
        </p>
        <div className="mt-5 space-y-4">
          <label className="block">
            <span className="eyebrow">Payer</span>
            <select
              value={subsidyForm.payer_id}
              onChange={(e) => setSubsidyForm({ ...subsidyForm, payer_id: e.target.value })}
              data-testid="billing-subsidy-payer"
              className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm bg-white focus:outline-none focus:border-[#1F3A2A]"
            >
              {subsidyPayers.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="eyebrow">Amount</span>
            <input
              type="number"
              step="0.01"
              data-testid="billing-subsidy-amount"
              value={subsidyForm.amount}
              onChange={(e) => setSubsidyForm({ ...subsidyForm, amount: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm focus:outline-none focus:border-[#1F3A2A]"
            />
          </label>
          <label className="block">
            <span className="eyebrow">Reference (optional)</span>
            <input
              type="text"
              data-testid="billing-subsidy-reference"
              value={subsidyForm.reference}
              onChange={(e) => setSubsidyForm({ ...subsidyForm, reference: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm focus:outline-none focus:border-[#1F3A2A]"
            />
          </label>
        </div>
        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            onClick={() => setSubsidyOpen(false)}
            className="text-sm text-[#6B7280] hover:text-[#1A1B2E]"
          >
            {t("billing.common.cancel")}
          </button>
          <button
            onClick={() =>
              op(
                () =>
                  api.post(`/admin/billing/invoices/${id}/subsidy-splits`, {
                    payer_id: subsidyForm.payer_id,
                    amount: Number(subsidyForm.amount),
                    reference: subsidyForm.reference || null,
                  }),
                "Subsidy split added."
              ).then(() => setSubsidyOpen(false))
            }
            data-testid="billing-subsidy-submit"
            className="inline-flex items-center gap-2 rounded-full bg-[#1F3A2A] text-white text-sm font-medium px-4 py-2 hover:bg-[#132C17]"
          >
            Add split
          </button>
        </div>
      </Modal>
    </main>
  );
}

function Meta({ label, value }) {
  return (
    <div className="rounded-2xl border border-[#EEF0F4] bg-white p-4">
      <div className="eyebrow">{label}</div>
      <div className="mt-1 text-sm text-[#1A1B2E]">{value}</div>
    </div>
  );
}
